import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import type { DocumentResponse, DocumentSortColumn } from '@vagusx/clients';
import type { FilterState } from '@/components/filter-bar/filter-bar';
import { useDocuments } from '@/hooks/use-documents';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { formatDate, formatDateShort } from '@/lib/format';
import { DocumentFilterBar } from './DocumentFilterBar';
import {
  filterStateToApiParams,
  filterStateToUrl,
  urlToFilterState,
} from './document-filter-utils';
import { usePersistentDocumentFilters } from './use-persistent-document-filters';
import { DrilldownBanner } from '@/components/dashboard/drilldown-banner';

const URGENCY_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  routine: 'outline',
  urgent: 'destructive',
  stat: 'destructive',
};

const SORTABLE_COLUMNS = new Set<DocumentSortColumn>([
  'documentDate',
  'patientName',
  'authNumber',
  'urgency',
]);

function isSortableColumn(id: string): id is DocumentSortColumn {
  return SORTABLE_COLUMNS.has(id as DocumentSortColumn);
}

/**
 * True when referrals are part of the visible result set — used to gate
 * the referral-specific columns (Auth #, Urgency). Returns true in two
 * cases:
 *   - Referral is explicitly selected in the multi-select filter.
 *   - No classification filter is active (all doc types showing, so
 *     referrals are among them).
 *
 * When the filter is narrowed to non-referral types only (e.g. denials),
 * those Auth #/Urgency cells would all be empty — so we hide the columns.
 */
function referralIncluded(state: FilterState): boolean {
  const f = state.classification;
  if (!f || f.type !== 'multi-select') return true;
  return f.values.includes('referral');
}

/**
 * Hide the Type column when the user has narrowed the filter to a
 * single classification — every visible row would have the same value
 * and the column would just be visual noise. Show it whenever the user
 * is mixing types or has cleared the filter.
 */
function multipleClassificationsActive(state: FilterState): boolean {
  const f = state.classification;
  if (f?.type !== 'multi-select') return true;
  return f.values.length !== 1;
}

function buildColumns(
  showReferralColumns: boolean,
  showTypeColumn: boolean
): ColumnDef<DocumentResponse>[] {
  const cols: ColumnDef<DocumentResponse>[] = [
    {
      accessorKey: 'patientName',
      header: ({ column }) => <SortableHeader column={column}>Patient</SortableHeader>,
      cell: ({ row }) => {
        // DOB rendered as a small muted "DOB: mm/dd/yyyy" under the
        // name — common medical-UI pattern for distinguishing patients
        // with the same name. mm/dd/yyyy keeps the line short enough
        // to fit alongside the name without wrapping the column.
        const dob = row.original.patientDob;
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {row.original.patientName || <span className="text-muted-foreground">Unknown</span>}
            </span>
            {dob && (
              <span className="text-xs text-muted-foreground">DOB: {formatDateShort(dob)}</span>
            )}
          </div>
        );
      },
    },
  ];

  if (showTypeColumn) {
    cols.push({
      accessorKey: 'classification',
      header: 'Type',
      cell: ({ row }) => {
        const c = row.original.classification;
        if (!c) return <span className="text-muted-foreground">&mdash;</span>;
        return (
          <Badge variant="outline" className="capitalize">
            {c.replace(/_/g, ' ')}
          </Badge>
        );
      },
    });
  }

  if (showReferralColumns) {
    // Auth # on top, urgency badge underneath — same compact two-line
    // pattern as Patient + DOB. Saves a column slot and keeps urgency
    // visually adjacent to the auth it belongs to.
    cols.push({
      accessorKey: 'authNumber',
      header: ({ column }) => <SortableHeader column={column}>Auth #</SortableHeader>,
      cell: ({ row }) => {
        const auth = row.original.authNumber;
        const urgency = row.original.urgency;
        return (
          <div className="flex flex-col items-start gap-0.5">
            <span>{auth || <span className="text-muted-foreground">&mdash;</span>}</span>
            {urgency && (
              <Badge
                variant={URGENCY_VARIANTS[urgency] ?? 'outline'}
                className="capitalize text-xs"
              >
                {urgency}
              </Badge>
            )}
          </div>
        );
      },
    });
  }

  cols.push({
    accessorKey: 'referringProviderName',
    header: ({ column }) => <SortableHeader column={column}>Referring Provider</SortableHeader>,
    cell: ({ row }) =>
      row.original.referringProviderName || <span className="text-muted-foreground">&mdash;</span>,
  });
  cols.push({
    accessorKey: 'healthPlan',
    header: ({ column }) => <SortableHeader column={column}>Health Plan</SortableHeader>,
    cell: ({ row }) =>
      row.original.healthPlan || <span className="text-muted-foreground">&mdash;</span>,
  });
  cols.push({
    accessorKey: 'documentDate',
    header: ({ column }) => <SortableHeader column={column}>Date</SortableHeader>,
    cell: ({ row }) => formatDate(row.original.documentDate),
  });

  return cols;
}

export default function DocumentsPage(): React.ReactElement {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;

  // Drilldown mode: when arriving from the dashboard's `Referrals`
  // tile, the URL is authoritative for this view and we deliberately
  // DON'T mutate the user's saved documents.list preference. The
  // persistent-filter hook becomes inert; the drilldown banner
  // renders at the top.
  const fromDashboard = search.from === 'dashboard';

  // Cross-session filter persistence: hydrate from server once on bare-URL
  // mount (redirect-once), debounced save on filter changes. URL stays
  // the in-session source of truth.
  const { waitingForHydrate } = usePersistentDocumentFilters({
    search,
    navigate,
    enabled: !fromDashboard,
  });

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  // Search term lives in the URL (shareable links + browser back/forward
  // sync the input). `SearchInput` debounces its onValueChange callback
  // internally, so we write to the URL once per debounced keystroke and
  // don't need a second debounce here.
  const urlSearchTerm = search.searchTerm ?? '';

  const [sorting, setSorting] = useState<SortingState>([{ id: 'documentDate', desc: true }]);

  // Extract individual primitive values from search for stable useMemo deps.
  // `useSearch()` returns a new object reference on every render.
  const urlUrgency = search.urgency;
  const urlClassification = search.classification;
  const urlReferring = search.referringProviderName;
  const urlHealthPlan = search.healthPlan;
  const urlDateFrom = search.dateFrom;
  const urlDateTo = search.dateTo;

  const filterState = useMemo(
    () => urlToFilterState(search),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- individual primitives for stable reference
    [urlUrgency, urlClassification, urlReferring, urlHealthPlan, urlDateFrom, urlDateTo]
  );

  const filterParams = filterStateToApiParams(filterState);
  const sortHead = sorting[0];
  const sortId = sortHead?.id;
  const sortBy = sortId && isSortableColumn(sortId) ? sortId : undefined;
  const sortOrder = sortHead ? (sortHead.desc ? 'desc' : 'asc') : undefined;

  // Column set is driven by two filter-derived flags:
  //   - Auth # / Urgency: only when referral is among the selected
  //     types (those fields are referral-specific).
  //   - Type: hidden when the filter is a single classification (every
  //     row would show the same value), shown when mixing types.
  // Recomputed only when the gates flip, not on every filter change.
  const showReferralColumns = referralIncluded(filterState);
  const showTypeColumn = multipleClassificationsActive(filterState);
  const columns = useMemo(
    () => buildColumns(showReferralColumns, showTypeColumn),
    [showReferralColumns, showTypeColumn]
  );

  // Gate the data query while we're still waiting for a bare-URL hydrate
  // — otherwise we fire with URL-defaults just to refetch moments later
  // after the redirect lands.
  const { data, isLoading, error } = useDocuments(
    {
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      searchTerm: urlSearchTerm || undefined,
      sortBy,
      sortOrder,
      ...filterParams,
    },
    { enabled: !waitingForHydrate }
  );

  // Build a navigate-with-merge helper so callers can change one URL
  // facet without clobbering the others. Pages with multiple URL-driven
  // facets all need this; centralizing here keeps the merge order
  // (current → updates) consistent.
  const updateSearchUrl = useCallback(
    (updates: Record<string, string | undefined>): void => {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries({ ...search, ...updates })) {
        if (v !== undefined && v !== '') next[k] = v;
      }
      navigate({ to: '/documents', search: next, replace: true });
    },
    [navigate, search]
  );

  const handleSearchChange = useCallback(
    (value: string): void => {
      // SearchInput already debounced this value; persist to URL directly.
      updateSearchUrl({ searchTerm: value || undefined });
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [updateSearchUrl]
  );

  const handleFilterChange = useCallback(
    (state: FilterState): void => {
      const nextFilterUrl = filterStateToUrl(state);
      // Preserve the ephemeral searchTerm; nuke any pagination cursor
      // since the result set has shifted under it.
      const merged: Record<string, string> = {};
      if (search.searchTerm) merged.searchTerm = search.searchTerm;
      for (const [k, v] of Object.entries(nextFilterUrl)) {
        if (v !== undefined && v !== '') merged[k] = v;
      }
      navigate({ to: '/documents', search: merged, replace: true });
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [navigate, search.searchTerm]
  );

  const handleRowClick = (document: DocumentResponse): void => {
    navigate({ to: '/documents/$id', params: { id: document.id } });
  };

  return (
    <div className="flex h-full flex-col">
      {/*
        DrilldownBanner renders only when ?from=dashboard is in the URL,
        so dropping it in unconditionally is safe — direct navigations
        see nothing. Provides "← Back to dashboard" + "× Clear filter"
        when the user arrived via the dashboard's Referrals tile.
      */}
      <DrilldownBanner />
      <TablePage
        title="Documents"
        description="Authorization and referral documents"
        columns={columns}
        data={data?.items ?? []}
        total={data?.total}
        // While `waitingForHydrate` is true the underlying query is `enabled:
        // false` (so `isLoading` is false, not "loading"). OR the two so the
        // table shows a loading state during the brief redirect window
        // instead of flashing the empty-state.
        isLoading={isLoading || waitingForHydrate}
        error={error}
        searchPlaceholder="Search"
        onRowClick={handleRowClick}
        emptyMessage="No documents found."
        errorMessage="Failed to load documents."
        pagination={pagination}
        onPaginationChange={setPagination}
        onSearchChange={handleSearchChange}
        searchValue={urlSearchTerm}
        sorting={sorting}
        onSortingChange={setSorting}
        filterBar={
          <DocumentFilterBar filterState={filterState} onFilterChange={handleFilterChange} />
        }
      />
    </div>
  );
}
